import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapEvent, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';

import ErrorBoundary from '@rover/components/src/errorHandling/ErrorBoundary';
import GeoMap, { MapMetadata } from '@rover/react-lib/src/components/GeoMap';
import { GeoMapLoadTilesLoadedEvent } from '@rover/react-lib/src/components/GeoMap/analyticsEvents';
import GeoMapErrorMessage from '@rover/react-lib/src/components/GeoMap/GeoMapErrorMessage';
import { DEFAULT_MAP_ZOOM } from '@rover/react-lib/src/components/GeoMap/types';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type {
  SearchMapData,
  SearchResult,
} from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { LatLng } from '@rover/types/src/LatLng';

import { EventTags, SearchMapMarkerClickedEvent, SearchMapZoomedEvent } from './analyticsEvents';
import ResultMarker from './ResultMarker';

type Props = {
  center?: LatLng | null;
  mapData: SearchMapData;
  searchResults: SearchResult[];
  onMapMoved: (arg0: MapMetadata) => void;
  searchInitiatedByMap: boolean;
  onMarkerClick: (index: number) => void;
  children?: React.ReactNode;
  showRankOnMarker: boolean;
  baseTags: EventTags;
};

const getMarkerTitle = ({ shortName, city, state, neighborhood, zip }: SearchResult): string => {
  let location = `${city}, ${state}`;
  if (neighborhood) location = `${neighborhood}, ${location}`;
  else location = `${location}, ${zip}`;
  return `${shortName}: ${location}`;
};

/**
 * Determines if the new map position is different enough from the previous
 * position. If so, we can decide to fire a search.
 */
export const passesThreshold = (currentMetadata: MapMetadata, nextMetadata: MapMetadata): boolean =>
  Object.keys(currentMetadata).some((key) => {
    const currentMetadataValue = currentMetadata[key].toPrecision(5);
    const nextMetadataValue = nextMetadata[key].toPrecision(5);
    return currentMetadataValue !== nextMetadataValue;
  });

const extractMapMetadata = (map: google.maps.Map): MapMetadata | null => {
  const bounds = map.getBounds();
  const newCenter = map.getCenter();
  if (!bounds || !newCenter) {
    return null;
  }

  const metadata: MapMetadata = {
    zoomlevel: map.getZoom() || DEFAULT_MAP_ZOOM,
    centerlat: newCenter.lat(),
    centerlng: newCenter.lng(),
    minlat: bounds.getSouthWest().lat(),
    minlng: bounds.getSouthWest().lng(),
    maxlat: bounds.getNorthEast().lat(),
    maxlng: bounds.getNorthEast().lng(),
  };

  return metadata;
};

const SearchMap: React.FC<Props> = (props) => {
  const {
    center: propsCenter,
    mapData,
    searchResults,
    onMapMoved,
    searchInitiatedByMap,
    onMarkerClick,
    children,
    showRankOnMarker,
    baseTags,
  } = props;

  // Refs for mutable values that don't trigger re-renders
  const map = useMap();
  const [refreshingFromFilter, setRefreshingFromFilter] = useState(false);
  const apiIsLoaded = useApiIsLoaded();

  const didInitialRender = useRef(false);
  const searchMapMetadataRef = useRef<MapMetadata | null | undefined>(undefined);
  const updatingRef = useRef(false);

  const onSearchResultsChanged = useCallback(
    ({ initialRender = false, retry = -1 }): void => {
      // Don't update the map's location if we did a map-based search
      if (searchInitiatedByMap && !initialRender) {
        setRefreshingFromFilter(false);
        return;
      }

      // Retry until google.maps is loaded
      if (!apiIsLoaded && retry > 0) {
        setTimeout(
          () =>
            onSearchResultsChanged({
              initialRender,
              retry: retry - 1,
            }),
          100
        );
        return;
      }

      // When out of retries, just stop and let the library handle errors
      if (retry === 0) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();

      if (mapData.viewport) {
        const { minlat, maxlat, minlng, maxlng } = mapData.viewport;
        bounds.extend(new google.maps.LatLng(minlat, minlng));
        bounds.extend(new google.maps.LatLng(maxlat, maxlng));
      }

      updatingRef.current = true;
      map?.fitBounds(bounds);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchInitiatedByMap, mapData.zoomMethod, mapData.viewport, apiIsLoaded, map]
  );

  useEffect(() => {
    setRefreshingFromFilter(true);
  }, [propsCenter]);

  /**
   * Fires on load and when the map becomes idle after panning or zooming. We
   * then check to see if the map has moved sufficiently to fire a search
   */
  const handleIdle = ({ map: updatedMap }: MapEvent): void => {
    const updatedMapMetadata = extractMapMetadata(updatedMap);

    if (
      updatedMapMetadata &&
      searchMapMetadataRef.current &&
      !updatingRef.current &&
      passesThreshold(searchMapMetadataRef.current, updatedMapMetadata) &&
      !refreshingFromFilter
    ) {
      onMapMoved(updatedMapMetadata);
    }

    if (updatedMapMetadata && !searchMapMetadataRef.current) {
      searchMapMetadataRef.current = updatedMapMetadata;
    }

    setRefreshingFromFilter(false);
    updatingRef.current = false;
  };

  useEffect(() => {
    if (!apiIsLoaded) {
      return;
    }
    onSearchResultsChanged({});
  }, [apiIsLoaded, map, mapData, onSearchResultsChanged]);

  const dynamicMapMarkers = useMemo((): JSX.Element[] => {
    if (!map) return [];
    return searchResults.map((result, index) => {
      return (
        <ResultMarker
          {...result}
          key={result.personOpk}
          index={index}
          title={getMarkerTitle(result)}
          onClick={() => {
            emitAnalyticsEvent(new SearchMapMarkerClickedEvent(baseTags));
            onMarkerClick(index);
          }}
          showRank={showRankOnMarker}
        />
      );
    });
  }, [baseTags, map, onMarkerClick, searchResults, showRankOnMarker]);

  useEffect(() => {
    if (!apiIsLoaded || didInitialRender.current) {
      return;
    }

    didInitialRender.current = true;
    onSearchResultsChanged({
      initialRender: true,
      retry: -1,
    });
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    document.querySelector('[aria-label="Map"]')?.setAttribute('aria-label', 'Sitter Location');
  }, [apiIsLoaded, onSearchResultsChanged]);

  return (
    <ErrorBoundary location="SearchMap" messageComponent={<GeoMapErrorMessage />}>
      <GeoMap
        onIdle={handleIdle}
        onZoomChanged={() => {
          emitAnalyticsEvent(new SearchMapZoomedEvent(baseTags));
        }}
        onTilesLoaded={() =>
          emitAnalyticsEvent(
            new GeoMapLoadTilesLoadedEvent({
              success: true,
            })
          )
        }
      >
        {dynamicMapMarkers}
        {children}
      </GeoMap>
    </ErrorBoundary>
  );
};

export default SearchMap;
